import { appFactory } from 'factories/apps';
import type { InvoiceEditorOption } from 'factories/invoices';
import {
  filterInvoicesFactory,
  getInvoiceOptions,
  invoiceEditorConnectionsFactory,
  invoicePreviewToReadableString,
  invoiceRulesFactory,
  mergePreviewProjectIds,
  sortInvoicesFactory,
} from 'factories/invoices';
import { isEmpty } from 'lodash-es';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import {
  getAccountCurrency,
  getAccountFeatures,
  getAreAzureADIntegrationEnabled,
  getArePremiumIntegrationsEnabled,
  getCurrentAccount,
} from 'redux/selectors/accounts';
import { getActiveClients, getClients } from 'redux/selectors/clients';
import { getCurrenciesById } from 'redux/selectors/currencies';
import { getAllActiveProjects, getProjectVariantMap } from 'redux/selectors/projects';
import { getUserPermissions, getUserTimeFormat } from 'redux/selectors/user';
import { getNonDeletedUsers } from 'redux/selectors/users';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { AppAccount, App as AppType } from 'types/apps';
import { InvoiceStatus } from 'types/invoice';
import type { UnionToIntersection } from 'types/utility';
import { formatCurrency } from 'utils/currency';
import { keyBy, notNull } from 'utils/typesafe';
import { extractSingleCurrency } from './invoice.utils';

const readInvoiceRules = (state: IState) => state.invoice.rules;
const readInvoiceRulesDraft = (state: IState) => state.invoice.rulesDraft;
const readInvoiceResult = (state: IState) => state.invoice.result;
const readInvoiceEntities = (state: IState) => state.invoice.entities;
const readInvoiceDraft = (state: IState) => state.invoice.draft;
const readInvoiceXhrStatus = (state: IState) => state.invoice.xhrStatus;
const readInvoiceSortBy = (state: IState) => state.invoice.sortBy;
const readInvoicePagination = (state: IState) => state.invoice.pagination;
const readInvoiceQuery = (state: IState) => state.invoice.query;
const readCreateInvoicePreview = (state: IState) => state.invoice.preview;
const readCurrentInvoiceId = (state: IState) => state.invoice.currentInvoiceId;

// Avoid circular deps :-/
const readAppAccounts = (state: IState) => state.apps.accounts;
const getAppsEntities = (state: IState) => state.apps.appsEntities;
const getAppsResult = (state: IState) => state.apps.appsResult;

const getApps = createSelector(
  [getAppsEntities, getAppsResult],
  (entities, result): Array<AppType> => result.map(id => entities[id])
);

const getEnhancedApps = createSelector(
  [
    getApps,
    getUserTimeFormat,
    getArePremiumIntegrationsEnabled,
    getAreAzureADIntegrationEnabled,
    getAccountFeatures,
  ],
  (apps, timeFormat, canUsePremiumIntegrations, canUseAzureADIntegration, accountFeatures) => {
    return apps.map(app =>
      appFactory(app, {
        timeFormat,
        canUsePremiumIntegrations,
        canUseAzureADIntegration,
        accountFeatures,
      })
    );
  }
);

export const getCreateInvoicePreview = createSelector(
  [readCreateInvoicePreview, getProjectVariantMap, getAccountCurrency],
  (preview, variantMap, accountCurrency) => {
    const projects = mergePreviewProjectIds(
      preview?.project_ids,
      preview?.skipped_project_ids,
      variantMap
    );

    const singleCurrency = extractSingleCurrency(projects);
    const formatValue = (value: number) => formatCurrency(singleCurrency ?? accountCurrency, value);
    const readableString = invoicePreviewToReadableString(preview, formatValue);
    return {
      invoiceCount: preview?.invoices_count,
      projects,
      error: preview?.error,
      skippedProjectIdsCount: preview?.skipped_project_ids.length || 0,
      readableString,
    };
  }
);

export const getExportableApps = createSelector([getEnhancedApps], apps =>
  apps.filter(app => app.featureRequirements?.includes('invoices'))
);

export const getExportableAppIds = createSelector([getExportableApps], apps =>
  apps.map(app => app.appId)
);

const getAppAccounts = readAppAccounts;

export const getSortBy = readInvoiceSortBy;

export const getInvoiceLoading = createSelector([readInvoiceXhrStatus], status => {
  const xhrState = xhrStatusUIState(status);
  return xhrState.isLoading;
});

export const getInvoiceSaving = createSelector([readInvoiceXhrStatus], status => {
  const xhrState = xhrStatusUIState(status);
  return xhrState.isSaving;
});

export const getInvoiceRules = readInvoiceRules;

export const getInvoiceEntities = readInvoiceEntities;

export const getInvoices = createSelector(
  [getInvoiceEntities, readInvoiceResult],
  (entities, result) => {
    return result.map(r => entities[r]).filter(notNull);
  }
);

export const getInvoiceDraftStatus = createSelector([readInvoiceDraft], draft => {
  return {
    exists: draft != null,
  };
});

export const getInvoiceDraft = readInvoiceDraft;

export const getInvoiceRulesDraft = createSelector(
  [readInvoiceRulesDraft, getCurrentAccount],
  (rulesDraft, account) => {
    if (!rulesDraft) {
      return invoiceRulesFactory(account);
    }
    return rulesDraft;
  }
);

export const getInvoicePagination = readInvoicePagination;

export const getInvoiceQuery = readInvoiceQuery;

export const getSortedInvoices = createSelector(
  [getSortBy, getInvoices, getInvoiceQuery],
  (sortBy, invoices, query) => {
    return sortInvoicesFactory(filterInvoicesFactory(invoices, query), sortBy);
  }
);

export const getCurrentInvoiceId = readCurrentInvoiceId;

export const getCurrentInvoiceIdById = createSelector(
  [getCurrentInvoiceId, getInvoiceEntities],
  (id, entities) => {
    if (id == null) {
      return null;
    }
    return entities[id] || null;
  }
);

export const getAppAccountsForExportableApps = createSelector(
  [getAppAccounts, getExportableApps],
  (accounts, apps): Record<number, Array<AppAccount>> => {
    if (!apps.length) {
      return {};
    }
    return apps.reduce((prev: Record<number, Array<AppAccount>>, curr) => {
      const id = curr.appId;
      const account = accounts[id];
      if (account) {
        return {
          ...prev,
          [id]: account,
        };
      }
      return prev;
    }, {});
  }
);

export const getInvoicePreview = createSelector(
  [
    getCurrentInvoiceId,
    getCurrentInvoiceIdById,
    getInvoiceLoading,
    getAppAccountsForExportableApps,
    getExportableApps,
  ],
  (invoiceId, invoice, isLoading, appAccounts, exportableApps) => {
    const { exportOptions, invoiceListOptions } = getInvoiceOptions(
      invoice,
      keyBy(exportableApps, 'appId'),
      appAccounts
    );
    const invoiceIsSynced =
      invoice?.status !== InvoiceStatus.LocalDraft && invoice?.status !== InvoiceStatus.Void;
    const invoiceProviderName = invoiceIsSynced
      ? invoice?.external_references?.[0]?.provider_type
      : undefined;

    const provider = invoiceProviderName
      ? exportableApps.find(app => app.provider === invoiceProviderName)
      : null;

    return {
      invoiceListOptions,
      exportOptions,
      invoiceId,
      invoice,
      isLoading,
      provider,
    };
  }
);

export const getInvoiceListRoute = createSelector(
  [
    getSortedInvoices,
    getInvoicePagination,
    getInvoiceLoading,
    getAppAccountsForExportableApps,
    getExportableApps,
    getInvoiceQuery,
    getSortBy,
    getUserPermissions,
    getAllActiveProjects,
    getActiveClients,
    getNonDeletedUsers,
    getCurrenciesById,
  ],
  (
    invoices,
    pagination,
    isLoading,
    appAccounts,
    apps,
    query,
    sortBy,
    permissions,
    projects,
    clients,
    users,
    currencyLookup
  ) => {
    const hasAppAccounts = !isEmpty(appAccounts);
    const isConnectedToExportableApp = apps.some(app => app.integrationsCount && app.ui.isEnabled);
    return {
      sortBy,
      query,
      hasAppAccounts,
      apps,
      invoices,
      maybeHasMore: pagination.maybeHasMore,
      pagination,
      isLoading,
      appAccounts,
      appEntities: keyBy(apps, 'appId'),
      isConnectedToExportableApp,
      permissions,
      projects,
      clients,
      users,
      currencyLookup,
    };
  }
);

export const getInvoiceEditor = createSelector(
  [
    getInvoiceDraft,
    getCurrentAccount,
    getClients,
    getAllActiveProjects,
    getInvoiceSaving,
    getAppAccountsForExportableApps,
    getExportableApps,
    getCreateInvoicePreview,
  ],
  (draft, account, clients, projects, isSaving, appAccounts, apps, preview) => {
    const appEntities = keyBy(apps, 'appId');
    const exportOptions: Array<UnionToIntersection<InvoiceEditorOption>> =
      invoiceEditorConnectionsFactory(appAccounts, appEntities);

    const listOptions = exportOptions.filter(o => o.type !== 'header');
    const connection = listOptions.find(option => option.id === draft?.connectionId);
    return {
      connection,
      draft,
      account,
      clients,
      projects,
      isSaving,
      exportOptions,
      preview,
    };
  }
);
